import { useEffect, useRef } from 'react';

type ScrollToElementOptions = {
  delay?: number;
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
  inline?: ScrollLogicalPosition;
};

export default function useScrollToElement<T extends HTMLElement = HTMLElement>(
  value: string | null,
  options: ScrollToElementOptions = {},
) {
  const elementRef = useRef<T>(null);

  useEffect(() => {
    let timeoutId: number | undefined;
    let retryTimeoutId: number | undefined;

    if (value !== null && elementRef.current) {
      const scrollToElement = (attempt = 0) => {
        const element = elementRef.current;
        if (!element) return;

        element.scrollIntoView({
          behavior: attempt === 0 ? (options.behavior || 'smooth') : 'instant',
          block: options.block || 'center',
          inline: options.inline || 'nearest',
        });

        // Retry once after a brief delay to handle layout shifts
        if (attempt === 0) {
          retryTimeoutId = window.setTimeout(() => scrollToElement(1), 200);
        }
      };

      timeoutId = window.setTimeout(scrollToElement, options.delay || 100);
    }

    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      if (retryTimeoutId !== undefined) {
        clearTimeout(retryTimeoutId);
      }
    };
  }, [value, options]);

  return { elementRef };
}
